<template>
  <div class="settings-list">
    <!-- Header -->
    <div class="settings-header">
      <div class="header-left">
        <h2>System Settings</h2>
        <p class="subtitle">Manage application settings and overrides</p>
      </div>
      <div class="header-right">
        <md-button class="md-primary md-raised" @click="initializeDefaults">
          <md-icon>settings_backup_restore</md-icon>
          Restore Defaults
        </md-button>
        <md-button class="md-info md-raised" @click="refreshSettings">
          <md-icon>refresh</md-icon>
          Refresh
        </md-button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-bar">
      <div class="md-layout md-gutter">
        <div class="md-layout-item md-size-30">
          <md-field>
            <label>Search Settings</label>
            <md-input v-model="filters.search" @input="onSearchInput" placeholder="Search by key or description..." />
          </md-field>
        </div>
        <div class="md-layout-item md-size-25">
          <md-field>
            <label>Category</label>
            <md-select v-model="filters.category" @md-selected="applyFilters">
              <md-option value="">All Categories</md-option>
              <md-option v-for="category in categories" :key="category" :value="category">
                {{ category | capitalize }}
              </md-option>
            </md-select>
          </md-field>
        </div>
        <div class="md-layout-item md-size-25">
          <md-field>
            <label>Show Overrides Only</label>
            <md-select v-model="filters.has_overrides" @md-selected="applyFilters">
              <md-option :value="null">All Settings</md-option>
              <md-option :value="true">With Overrides</md-option>
              <md-option :value="false">Without Overrides</md-option>
            </md-select>
          </md-field>
        </div>
        <div class="md-layout-item md-size-20">
          <md-field>
            <label>Per Page</label>
            <md-select v-model="pagination.perPage" @md-selected="onPerPageChange">
              <md-option v-for="item in perPageOptions" :key="item" :value="item">
                {{ item }}
              </md-option>
            </md-select>
          </md-field>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="loading-container">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>

    <!-- Settings Table -->
    <div v-else class="settings-table-wrapper">
      <md-table v-if="filteredSettings && filteredSettings.length">
        <md-table-row>
          <md-table-head>Key</md-table-head>
          <md-table-head>Value</md-table-head>
          <md-table-head>Category</md-table-head>
          <md-table-head>Type</md-table-head>
          <md-table-head>Overrides</md-table-head>
          <md-table-head style="width: 200px;">Actions</md-table-head>
        </md-table-row>

        <md-table-row v-for="setting in filteredSettings" :key="setting.key">
          <md-table-cell>
            <div class="setting-key">
              <strong>{{ setting.key }}</strong>
              <div class="setting-description" v-if="setting.description">
                <small>{{ setting.description }}</small>
              </div>
            </div>
          </md-table-cell>
          <md-table-cell>
            <div class="setting-value">
              <span class="value-display">{{ formatValue(setting.value, setting.type) }}</span>
              <span v-if="setting.override_count > 0" class="override-indicator">
                <md-icon>arrow_right_alt</md-icon>
                Overridden
              </span>
            </div>
          </md-table-cell>
          <md-table-cell>
            <badge :type="getCategoryBadge(setting.category)">
              {{ setting.category || 'General' | capitalize }}
            </badge>
          </md-table-cell>
          <md-table-cell>
            <badge type="info">{{ setting.type }}</badge>
          </md-table-cell>
          <md-table-cell>
            <span v-if="setting.override_count > 0" class="override-count">
              {{ setting.override_count }}
              <badge type="warning" v-if="setting.active_override_count > 0">
                {{ setting.active_override_count }} active
              </badge>
            </span>
            <span v-else class="text-muted">None</span>
          </md-table-cell>
          <md-table-cell>
            <md-button class="md-icon-button md-primary" @click="editSetting(setting)" title="Edit">
              <md-icon>edit</md-icon>
            </md-button>
            <md-button class="md-icon-button md-info" @click="viewOverrides(setting)" title="Overrides">
              <md-icon>layers</md-icon>
            </md-button>
            <md-button class="md-icon-button md-warning" @click="resetToDefault(setting)" title="Reset to Default">
              <md-icon>restore</md-icon>
            </md-button>
          </md-table-cell>
        </md-table-row>
      </md-table>

      <div v-else class="empty-state">
        <md-icon>settings</md-icon>
        <p>No settings found</p>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-footer" v-if="meta.total > 0">
      <div class="pagination-info">
        Showing {{ from + 1 }} to {{ to }} of {{ meta.total }} entries
      </div>
      <pagination
          class="pagination-no-border pagination-success"
          v-model="pagination.currentPage"
          :per-page="pagination.perPage"
          :total="meta.total"
          @change="onPageChange"
      />
    </div>

    <!-- Edit Setting Modal -->
    <md-dialog :md-active.sync="modal.edit.active" class="edit-modal">
      <md-dialog-title>Edit Setting: {{ selectedSetting ? selectedSetting.key : '' }}</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <div class="setting-info">
            <p><strong>Description:</strong> {{ selectedSetting ? selectedSetting.description || 'No description' : '' }}</p>
            <p><strong>Type:</strong> {{ selectedSetting ? selectedSetting.type : '' }}</p>
            <p><strong>Category:</strong> {{ selectedSetting ? selectedSetting.category || 'General' : '' }}</p>
          </div>

          <md-field>
            <label>Value</label>
            <md-input
                v-if="selectedSetting && selectedSetting.type !== 'boolean' && selectedSetting.type !== 'json'"
                v-model="editValue"
                :type="getInputType(selectedSetting.type)"
            />
            <md-select
                v-else-if="selectedSetting && selectedSetting.type === 'boolean'"
                v-model="editValue"
            >
              <md-option :value="true">True</md-option>
              <md-option :value="false">False</md-option>
            </md-select>
            <md-textarea
                v-else-if="selectedSetting && selectedSetting.type === 'json'"
                v-model="editValue"
                rows="4"
                placeholder="Enter JSON value"
            />
          </md-field>

          <div v-if="selectedSetting && selectedSetting.type === 'json'" class="json-hint">
            <small>Enter valid JSON. Example: {"key": "value"}</small>
          </div>

          <div class="override-info" v-if="selectedSetting && selectedSetting.override_count > 0">
            <md-icon>warning</md-icon>
            <span>This setting has active overrides. Editing the base value will not affect overrides.</span>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="saveSetting" :disabled="isSaving">
          {{ isSaving ? 'Saving...' : 'Save' }}
        </md-button>
        <md-button class="md-danger" @click="closeEditModal">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Overrides Modal -->
    <md-dialog :md-active.sync="modal.overrides.active" class="overrides-modal">
      <md-dialog-title>
        Overrides for: {{ selectedSetting ? selectedSetting.key : '' }}
        <span class="subtitle">{{ selectedSetting ? selectedSetting.description : '' }}</span>
      </md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <div class="override-actions">
            <md-button class="md-primary md-raised" @click="showCreateOverride">
              <md-icon>add</md-icon> Add Override
            </md-button>
          </div>

          <div v-if="overrides && overrides.length > 0" class="overrides-list">
            <div v-for="override in overrides" :key="override.id" class="override-item">
              <div class="override-info">
                <div class="override-level">
                  <badge :type="getLevelBadge(override.override_type)">
                    {{ override.override_type | capitalize }}
                  </badge>
                  <span class="override-id">ID: {{ override.override_id }}</span>
                </div>
                <div class="override-value">
                  <strong>Value:</strong> {{ formatValue(override.value, selectedSetting ? selectedSetting.type : 'string') }}
                </div>
                <div class="override-meta" v-if="override.reason">
                  <span class="reason">{{ override.reason }}</span>
                </div>
                <div class="override-meta">
                  <span class="created-by">Created by: {{ override.created_by ? override.created_by.name || 'Unknown' : 'Unknown' }}</span>
                  <span class="created-at">{{ override.created_at | formatDate }}</span>
                  <span v-if="override.expires_at" class="expires-at">
                    Expires: {{ override.expires_at | formatDate }}
                  </span>
                  <badge :type="override.is_active ? 'success' : 'danger'">
                    {{ override.is_active ? 'Active' : 'Inactive' }}
                  </badge>
                </div>
              </div>
              <div class="override-actions">
                <md-button class="md-icon-button md-danger" @click="deleteOverride(override)" title="Delete">
                  <md-icon>delete</md-icon>
                </md-button>
              </div>
            </div>
          </div>

          <div v-else class="empty-overrides">
            <md-icon>layers_clear</md-icon>
            <p>No overrides defined for this setting</p>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="closeOverridesModal">Close</md-button>
      </md-dialog-actions>
    </md-dialog>

    <!-- Create Override Modal -->
    <md-dialog :md-active.sync="modal.createOverride.active" class="create-override-modal">
      <md-dialog-title>Create Override</md-dialog-title>
      <md-dialog-content>
        <div class="modal-content">
          <p>Create an override for: <strong>{{ selectedSetting ? selectedSetting.key : '' }}</strong></p>

          <md-field>
            <label>Override Level</label>
            <md-select v-model="newOverride.override_type" required>
              <md-option v-for="level in overrideLevels" :key="level" :value="level">
                {{ level | capitalize }}
              </md-option>
            </md-select>
          </md-field>

          <md-field>
            <label>Target ID</label>
            <md-input v-model="newOverride.override_id" type="number" required placeholder="Enter the ID of the target" />
          </md-field>

          <md-field>
            <label>Value</label>
            <md-input
                v-if="selectedSetting && selectedSetting.type !== 'boolean' && selectedSetting.type !== 'json'"
                v-model="newOverride.value"
                :type="getInputType(selectedSetting.type)"
            />
            <md-select
                v-else-if="selectedSetting && selectedSetting.type === 'boolean'"
                v-model="newOverride.value"
            >
              <md-option :value="true">True</md-option>
              <md-option :value="false">False</md-option>
            </md-select>
            <md-textarea
                v-else-if="selectedSetting && selectedSetting.type === 'json'"
                v-model="newOverride.value"
                rows="3"
                placeholder="Enter JSON value"
            />
          </md-field>

          <md-field>
            <label>Reason (Optional)</label>
            <md-textarea v-model="newOverride.reason" rows="2" placeholder="Why is this override needed?" />
          </md-field>

          <md-field>
            <label>Expires At (Optional)</label>
            <md-input v-model="newOverride.expires_at" type="datetime-local" />
          </md-field>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="saveOverride" :disabled="isSaving">
          {{ isSaving ? 'Creating...' : 'Create Override' }}
        </md-button>
        <md-button class="md-danger" @click="closeCreateOverrideModal">Cancel</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Badge from '@/components/Badge.vue';
import Pagination from '@/components/Pagination';
import _ from 'lodash';

export default {
  name: 'SettingsList',
  components: {
    Badge: Badge,
    Pagination: Pagination
  },
  data: function() {
    return {
      filters: {
        search: '',
        category: '',
        has_overrides: null
      },
      pagination: {
        currentPage: 1,
        perPage: 15
      },
      perPageOptions: [10, 15, 25, 50, 100],
      selectedSetting: null,
      editValue: null,
      isSaving: false,
      modal: {
        edit: { active: false },
        overrides: { active: false },
        createOverride: { active: false }
      },
      newOverride: {
        override_type: '',
        override_id: null,
        value: null,
        reason: '',
        expires_at: null
      },
      searchTimeout: null
    };
  },
  computed: {
    ...mapGetters('settings', [
      'settings',
      'setting',
      'overrides',
      'meta',
      'isLoading',
      'error',
      'categories',
      'overrideLevels',
      'settingsByCategory'
    ]),

    filteredSettings: function() {
      var items = this.settings || [];
      var self = this;

      if (this.filters.search) {
        var search = this.filters.search.toLowerCase();
        items = items.filter(function(s) {
          return s.key.toLowerCase().includes(search) ||
              (s.description && s.description.toLowerCase().includes(search));
        });
      }

      if (this.filters.category) {
        items = items.filter(function(s) { return s.category === self.filters.category; });
      }

      if (this.filters.has_overrides !== null) {
        items = items.filter(function(s) {
          var hasOverrides = s.override_count > 0;
          return self.filters.has_overrides ? hasOverrides : !hasOverrides;
        });
      }

      return items;
    },

    from: function() {
      return this.pagination.perPage * (this.pagination.currentPage - 1);
    },
    to: function() {
      var highBound = this.from + this.pagination.perPage;
      return Math.min(highBound, this.meta.total || 0);
    }
  },
  created: function() {
    this.fetchCategories();
    this.fetchSettings();
  },
  methods: {
    ...mapActions('settings', [
      'fetchSettings',
      'fetchSetting',
      'updateSetting',
      'fetchOverrides',
      'createOverride',
      'deleteOverride',
      'resetToDefault',
      'initializeDefaults',
      'clearError'
    ]),

    fetchCategories: function() {
      var categories = new Set();
      var self = this;
      this.settings.forEach(function(s) {
        if (s.category) {
          categories.add(s.category);
        }
      });
      this.$store.commit('settings/SET_CATEGORIES', Array.from(categories));
    },

    fetchSettings: function() {
      var self = this;
      var params = {
        page: {
          number: this.pagination.currentPage,
          size: this.pagination.perPage
        }
      };

      if (this.filters.search) {
        params.filter = { search: this.filters.search };
      }

      this.fetchSettings(params)
          .then(function() {
            self.fetchCategories();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to fetch settings');
          });
    },

    onSearchInput: function() {
      var self = this;
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(function() {
        self.pagination.currentPage = 1;
        self.fetchSettings();
      }, 500);
    },

    applyFilters: function() {
      this.pagination.currentPage = 1;
      this.fetchSettings();
    },

    onPerPageChange: function() {
      this.pagination.currentPage = 1;
      this.fetchSettings();
    },

    onPageChange: function(page) {
      this.pagination.currentPage = page;
      this.fetchSettings();
    },

    refreshSettings: function() {
      this.fetchSettings();
      this.$store.dispatch('alerts/success', 'Settings refreshed');
    },

    editSetting: function(setting) {
      var self = this;
      this.fetchSetting(setting.key)
          .then(function(fullSetting) {
            self.selectedSetting = fullSetting;
            self.editValue = self.getEditableValue(fullSetting.value, fullSetting.type);
            self.modal.edit.active = true;
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to load setting details');
          });
    },

    closeEditModal: function() {
      this.modal.edit.active = false;
      this.selectedSetting = null;
      this.editValue = null;
    },

    saveSetting: function() {
      var self = this;
      if (!this.selectedSetting) return;

      this.isSaving = true;
      var payload = {
        key: this.selectedSetting.key,
        value: this.parseValue(this.editValue, this.selectedSetting.type),
        type: this.selectedSetting.type,
        description: this.selectedSetting.description,
        category: this.selectedSetting.category
      };

      this.updateSetting(payload)
          .then(function() {
            self.$store.dispatch('alerts/success', 'Setting updated successfully');
            self.closeEditModal();
            self.fetchSettings();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to update setting');
          })
          .finally(function() {
            self.isSaving = false;
          });
    },

    viewOverrides: function(setting) {
      var self = this;
      this.selectedSetting = setting;
      this.fetchOverrides(setting.key)
          .then(function() {
            self.modal.overrides.active = true;
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to load overrides');
          });
    },

    closeOverridesModal: function() {
      this.modal.overrides.active = false;
      this.selectedSetting = null;
    },

    showCreateOverride: function() {
      this.newOverride = {
        override_type: '',
        override_id: null,
        value: this.getEditableValue(this.selectedSetting ? this.selectedSetting.value : null, this.selectedSetting ? this.selectedSetting.type : 'string'),
        reason: '',
        expires_at: null
      };
      this.modal.createOverride.active = true;
    },

    closeCreateOverrideModal: function() {
      this.modal.createOverride.active = false;
      this.newOverride = {
        override_type: '',
        override_id: null,
        value: null,
        reason: '',
        expires_at: null
      };
    },

    saveOverride: function() {
      var self = this;
      if (!this.selectedSetting || !this.newOverride.override_type || !this.newOverride.override_id) {
        this.$store.dispatch('alerts/warning', 'Please fill all required fields');
        return;
      }

      this.isSaving = true;
      var payload = {
        setting_key: this.selectedSetting.key,
        value: this.parseValue(this.newOverride.value, this.selectedSetting.type),
        override_type: this.newOverride.override_type,
        override_id: this.newOverride.override_id,
        reason: this.newOverride.reason,
        expires_at: this.newOverride.expires_at
      };

      this.createOverride(payload)
          .then(function() {
            self.$store.dispatch('alerts/success', 'Override created successfully');
            self.closeCreateOverrideModal();
            return self.fetchOverrides(self.selectedSetting.key);
          })
          .then(function() {
            return self.fetchSettings();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to create override');
          })
          .finally(function() {
            self.isSaving = false;
          });
    },

    deleteOverride: function(override) {
      var self = this;
      if (!confirm('Are you sure you want to delete this override?')) return;

      this.deleteOverride(override.id)
          .then(function() {
            self.$store.dispatch('alerts/success', 'Override deleted successfully');
            return self.fetchOverrides(self.selectedSetting.key);
          })
          .then(function() {
            return self.fetchSettings();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to delete override');
          });
    },

    resetToDefault: function(setting) {
      var self = this;
      if (!confirm('Reset "' + setting.key + '" to default value?')) return;

      this.resetToDefault(setting.key)
          .then(function() {
            self.$store.dispatch('alerts/success', 'Setting reset to default');
            self.fetchSettings();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to reset setting');
          });
    },

    initializeDefaults: function() {
      var self = this;
      if (!confirm('This will restore all settings to default values. Continue?')) return;

      this.initializeDefaults()
          .then(function() {
            self.$store.dispatch('alerts/success', 'Default settings restored');
            self.fetchSettings();
          })
          .catch(function(error) {
            self.$store.dispatch('alerts/error', 'Failed to restore defaults');
          });
    },

    getEditableValue: function(value, type) {
      if (type === 'json' && typeof value === 'object') {
        return JSON.stringify(value, null, 2);
      }
      return value;
    },

    parseValue: function(value, type) {
      if (type === 'boolean') {
        return value === true || value === 'true';
      }
      if (type === 'integer') {
        return parseInt(value);
      }
      if (type === 'json') {
        try {
          return JSON.parse(value);
        } catch (e) {
          return value;
        }
      }
      return value;
    },

    formatValue: function(value, type) {
      if (value === null || value === undefined) return 'N/A';
      if (type === 'boolean') {
        return value ? 'True' : 'False';
      }
      if (type === 'json') {
        return JSON.stringify(value);
      }
      return String(value);
    },

    getInputType: function(type) {
      var types = {
        'integer': 'number',
        'string': 'text',
        'text': 'text'
      };
      return types[type] || 'text';
    },

    getCategoryBadge: function(category) {
      var badges = {
        'attendance': 'primary',
        'system': 'info',
        'security': 'danger',
        'notification': 'warning'
      };
      return badges[category] || 'default';
    },

    getLevelBadge: function(level) {
      var badges = {
        'user': 'primary',
        'staff': 'info',
        'department': 'success',
        'workplace': 'warning',
        'station': 'danger'
      };
      return badges[level] || 'default';
    }
  },
  filters: {
    capitalize: function(value) {
      if (!value) return '';
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatDate: function(value) {
      if (!value) return '';
      var date = new Date(value);
      return date.toLocaleString();
    }
  }
};
</script>

<style scoped>
.settings-list {
  padding: 20px;
}

.settings-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0;
  color: #333;
}

.header-left .subtitle {
  color: #666;
  margin-top: 5px;
}

.header-right {
  display: flex;
  gap: 10px;
}

.filters-bar {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.settings-table-wrapper {
  overflow-x: auto;
}

.setting-key .setting-description {
  color: #666;
  font-size: 12px;
  margin-top: 2px;
}

.setting-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.value-display {
  font-family: monospace;
  font-size: 13px;
}

.override-indicator {
  color: #ff9800;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.override-indicator md-icon {
  font-size: 16px;
  width: 16px;
  height: 16px;
}

.override-count {
  display: flex;
  align-items: center;
  gap: 8px;
}

.text-muted {
  color: #999;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}

.empty-state md-icon {
  font-size: 64px;
  width: 64px;
  height: 64px;
  margin-bottom: 16px;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  font-size: 13px;
  color: #666;
}

/* Modal Styles */
.edit-modal .md-dialog,
.overrides-modal .md-dialog,
.create-override-modal .md-dialog {
  max-width: 700px;
  width: 95%;
}

.modal-content {
  padding: 10px 0;
}

.setting-info {
  background: #f5f5f5;
  padding: 12px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
}

.setting-info p {
  margin: 5px 0;
  font-size: 13px;
}

.json-hint {
  color: #666;
  font-size: 12px;
  margin-top: 5px;
}

.override-info {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  background: #fff3e0;
  border-radius: 4px;
  margin-top: 10px;
  color: #e65100;
  font-size: 13px;
}

.override-info md-icon {
  color: #e65100;
}

/* Overrides List */
.override-actions {
  margin-bottom: 15px;
}

.overrides-list {
  max-height: 400px;
  overflow-y: auto;
}

.override-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 10px;
  background: #fafafa;
}

.override-item:hover {
  background: #f5f5f5;
}

.override-info {
  flex: 1;
}

.override-level {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
}

.override-id {
  font-size: 12px;
  color: #666;
}

.override-value {
  margin-bottom: 5px;
  font-size: 14px;
}

.override-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: #666;
}

.override-meta .reason {
  color: #333;
  font-style: italic;
}

.override-meta .created-by,
.override-meta .created-at,
.override-meta .expires-at {
  display: inline-block;
}

.empty-overrides {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-overrides md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
  margin-bottom: 10px;
}

.override-item .override-actions {
  margin-left: 15px;
  display: flex;
  gap: 5px;
}

@media (max-width: 768px) {
  .settings-header {
    flex-direction: column;
    gap: 10px;
  }

  .header-right {
    width: 100%;
  }

  .header-right .md-button {
    flex: 1;
  }

  .override-item {
    flex-direction: column;
  }

  .override-item .override-actions {
    margin-left: 0;
    margin-top: 10px;
    align-self: flex-end;
  }
}
</style>